import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = React.createContext();


function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('session_token');
    if (token) {
      setSessionToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loginWithCredentials = async (userData, sessionToken) => {
    setUser(userData);
    setSessionToken(sessionToken);
    localStorage.setItem('session_token', sessionToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
  };

  const processSessionId = async (sessionId) => {
    try {
      setLoading(true);
      const response = await axios.post('/auth/session', { session_id: sessionId });
      
      const { user: userData, session_token } = response.data;
      
      setUser(userData);
      setSessionToken(session_token);
      localStorage.setItem('session_token', session_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
      
      toast.success(`Welcome back, ${userData.name}!`);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
    } catch (error) {
      console.error('Session processing failed:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setSessionToken(null);
      localStorage.removeItem('session_token');
      delete axios.defaults.headers.common['Authorization'];
      toast.success('Logged out successfully');
    }
  };

  const value = {
    user,
    setUser,
    loading,
    processSessionId,
    loginWithCredentials,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Components

export { AuthProvider, useAuth, AuthContext };
